import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { Search, X, Ticket, Building2, Package } from "lucide-react";

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ tickets: [], customers: [], parts: [] });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else { setQ(""); setResults({ tickets: [], customers: [], parts: [] }); }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults({ tickets: [], customers: [], parts: [] }); return; }
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(r.data);
      } catch { /* ignore */ }
      finally { setBusy(false); }
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  if (!open) return null;

  const go = (path) => { onClose(); navigate(path); };
  const has = results.tickets.length + results.customers.length + results.parts.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-[10vh]"
      onClick={onClose}
      data-testid="global-search-overlay"
    >
      <div className="w-full max-w-2xl sharp-card overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-border/60 px-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tickets, customers, parts…"
            className="flex-1 bg-transparent py-4 text-sm outline-none placeholder:text-muted-foreground"
          />
          <span className="hidden sm:inline text-[10px] font-mono text-muted-foreground border border-border/70 rounded px-1.5 py-0.5">ESC</span>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto scrollbar-thin">
          {!q && <p className="p-6 text-xs text-muted-foreground">Start typing to search across your entire operation.</p>}
          {q && !has && !busy && <p className="p-6 text-xs text-muted-foreground">No matches for &quot;{q}&quot;</p>}

          {results.tickets.length > 0 && (
            <Section title="Tickets" count={results.tickets.length}>
              {results.tickets.map((t) => (
                <Row key={t.id} icon={Ticket} onClick={() => go(`/app/tickets/${t.id}`)} testid={`search-ticket-${t.ticket_no}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{t.complaint}</p>
                      <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{t.ticket_no}</p>
                    </div>
                    <span className={`status-${t.status} rounded-md px-1.5 py-0.5 text-[10px] uppercase tracking-wider`}>{t.status}</span>
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {results.customers.length > 0 && (
            <Section title="Customers" count={results.customers.length}>
              {results.customers.map((c) => (
                <Row key={c.id} icon={Building2} onClick={() => go(`/app/customers/${c.id}`)} testid={`search-customer-${c.id}`}>
                  <p className="truncate text-sm font-medium">{c.company_name}</p>
                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{c.contact_person} · {c.mobile}</p>
                </Row>
              ))}
            </Section>
          )}

          {results.parts.length > 0 && (
            <Section title="Parts" count={results.parts.length}>
              {results.parts.map((p) => (
                <Row key={p.id} icon={Package} onClick={() => go(`/app/inventory`)} testid={`search-part-${p.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="truncate text-sm font-medium">{p.name}</p>
                      <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{p.sku || "no sku"} · ₹{p.unit_price}</p>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">{p.stock}</span>
                  </div>
                </Row>
              ))}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div className="border-b border-border/40 last:border-b-0">
      <div className="flex items-center justify-between border-b border-border/60 bg-secondary/30 px-4 py-1.5">
        <p className="label-eyebrow">{title}</p>
        <span className="text-[10px] text-muted-foreground">{count}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({ icon: Icon, children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex w-full items-start gap-3 border-b border-border/40 px-4 py-2.5 text-left hover:bg-secondary/50"
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">{children}</div>
    </button>
  );
}
